import { parse } from 'date-fns/parse';
import { format } from 'date-fns/format';
import { PiPhoneOutgoing } from 'react-icons/pi';
import {
  MdOutlineEmail,
  MdOutlineHome,
  MdOutlineBusinessCenter,
  MdOutlineEventAvailable,
  MdOutlineFactCheck,
  MdOutlineLocationOn,
  MdDoneAll,
  MdOutlineMyLocation,
} from 'react-icons/md';
import { OcsDeviceOutlineIcon } from '@icons';
import { ConditionalRender } from '@ui';
import { EmployeeComposed, EmployeeRole, LocationPreference } from '@/api/apis/resources';
import { formatPhoneNumber, formatTsaNumber, getLocationAddress } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { InfoBlock } from './InfoBlock';
import { InfoRow } from './InfoRow';
import { UserRoleChip } from './UserRoleChip';
import { RoleChip } from './RoleChip';

type UserInfoProps = {
  user: EmployeeComposed;
};

const SPECIALIZATION_CHIPS = [
  { label: 'CS Proctor', key: 'proctorOrganSpecializations' },
  { label: 'CS Shadow', key: 'shadowOrganSpecializations' },
] as const;

const SpecializationChips = ({ user }: { user: EmployeeComposed }) => {
  if (!user.roles.includes(EmployeeRole.ClinicalSpecialist)) return null;

  return (
    <>
      {SPECIALIZATION_CHIPS.map(({ label, key }) =>
        user[key]?.length ? (
          <RoleChip key={key} label={label} organs={user[key]} organBadgeProps={{ showBackground: false }} />
        ) : null,
      )}
    </>
  );
};

const formatDate = (value?: string) =>
  value ? format(parse(value, 'yyyy-MM-dd', new Date()), "MMMM dd',' yyyy") : '-';
const formatDateTime = (value?: string) => (value ? format(value, "MMMM dd',' yyyy") : '-');

export const UserInfo = ({ user }: UserInfoProps) => {
  const { resolveSubdivisionName } = useCountriesData();
  const { homeLocation, baseLocation, trackedLocation } = user;
  const homeLocationString = getLocationAddress(homeLocation, resolveSubdivisionName);
  const hireDate = formatDate(user.hireDate);
  const isLocationPreferenceBase = user.locationPreference === LocationPreference.Base;
  const isLocationPreferenceHome = user.locationPreference === LocationPreference.Home;
  const isClinicalSpecialist = user.roles.includes(EmployeeRole.ClinicalSpecialist);
  const isSpecialistSurgeonLeadRole =
    isClinicalSpecialist ||
    user.roles.includes(EmployeeRole.RetrievalSurgeon) ||
    user.roles.includes(EmployeeRole.ClinicalLead);

  const rolesValue = (
    <div className="flex flex-wrap gap-1">
      {user.roles.map((role) => (
        <UserRoleChip key={role} role={role} user={user} />
      ))}
      <SpecializationChips user={user} />
    </div>
  );

  return (
    <div className="flex py-6 justify-between">
      <InfoBlock title="Contact Information">
        <InfoRow
          name="Email"
          value={user.email}
          textLink={`mailto:${user.email}`}
          icon={<MdOutlineEmail size={28} />}
          withTooltip
        />
        <InfoRow
          name="Phone Number"
          value={user.phone ? formatPhoneNumber(user.phone) : ''}
          icon={<PiPhoneOutgoing size={28} />}
        />
      </InfoBlock>
      <InfoBlock title="Professional Information">
        <InfoRow name="Role" value={rolesValue} icon={<MdOutlineBusinessCenter size={28} />} />
        <InfoRow
          name="TSA Precheck Number"
          value={formatTsaNumber(user.tsaPrecheckNumber ?? '')}
          icon={<MdOutlineFactCheck size={28} />}
        />
        <ConditionalRender show={isSpecialistSurgeonLeadRole}>
          <InfoRow name="Hire Date" value={hireDate} icon={<MdOutlineEventAvailable size={28} />} />
        </ConditionalRender>
      </InfoBlock>
      <InfoBlock title="Location Information">
        <InfoRow
          name="Home Location"
          labelIcon={isLocationPreferenceHome || !isSpecialistSurgeonLeadRole ? <MdDoneAll size={18} /> : null}
          value={homeLocationString}
          icon={<MdOutlineHome size={28} />}
          withTruncate={false}
        />
        <ConditionalRender show={isSpecialistSurgeonLeadRole}>
          <InfoRow
            name="Base Location"
            labelIcon={isLocationPreferenceBase ? <MdDoneAll size={18} /> : null}
            value={baseLocation?.name}
            icon={<OcsDeviceOutlineIcon />}
            withTooltip
            withTruncate={false}
          />
        </ConditionalRender>
        <InfoRow
          name="Current Location"
          value={trackedLocation?.address || 'Location not available'}
          icon={<MdOutlineLocationOn size={28} />}
          withTruncate={false}
        />
        <ConditionalRender show={!!trackedLocation?.address}>
          <InfoRow
            name="Location Tracked"
            value={formatDateTime(trackedLocation?.trackedTime)}
            icon={<MdOutlineMyLocation size={28} />}
            withTruncate={false}
          />
        </ConditionalRender>
      </InfoBlock>
    </div>
  );
};
